import { intersection } from "lodash";

import { CurrentUser } from "features/auth/auth-slice";

export const hasAccessRestrictions = (
  roles?: string[],
  permissions?: string[]
) => roles || permissions;

export const hasRequiredAccess = (
  currentUser: CurrentUser | null,
  userRoles: string[],
  userPermissions: Record<string, boolean>,
  roles?: string[],
  permissions?: string[]
) =>
  Boolean(
    currentUser &&
      (Boolean(roles && intersection(roles, userRoles).length > 0) ||
        Boolean(permissions?.some((permission) => userPermissions[permission])))
  );

/**
 * Sections a "Financial Reports Only Access" user may still visit. `/settings` stays reachable so
 * the account menu's "My Settings" works (password, photo, notification preferences — the
 * Scheduler persona keeps it for the same reason); `/permission` stays reachable so an access
 * denial can render instead of redirect-looping with the guard. `/quickbooks` is conditional:
 * bookkeepers are exactly QuickBooks' persona, so it opens up when the caller says the user may
 * access QBO (the per-user grant + the practice having QuickBooks enabled — the same pair the
 * side menu checks).
 */
const REPORTS_ONLY_ALLOWED_PATH_PREFIXES = [
  "/reports",
  "/settings",
  "/permission"
];

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isPathAllowedForReportsOnly = (
  pathname: string,
  { canAccessQuickbooks = false }: { canAccessQuickbooks?: boolean } = {}
): boolean =>
  REPORTS_ONLY_ALLOWED_PATH_PREFIXES.some((prefix) =>
    matchesPrefix(pathname, prefix)
  ) ||
  (canAccessQuickbooks && matchesPrefix(pathname, "/quickbooks"));

export const parseJWT = (token: string | undefined | null) => {
  if (!token) return undefined;
  try {
    const [, base64Url] = token.split(".");

    if (!base64Url) {
      return undefined;
    }

    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");

    const decoder = new TextDecoder("utf-8");
    const binaryString = atob(base64);

    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    const jsonPayload = decoder.decode(bytes);

    return JSON.parse(jsonPayload);
  } catch {
    return undefined;
  }
};
